import React, { useState, useEffect } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Checkbox from "@mui/material/Checkbox";
import "bootstrap/dist/css/bootstrap.min.css";
import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment, TextField } from "@mui/material";
import { FadeLoader } from "react-spinners";

import { usePaginationState } from "../../hooks/usePaginationState";

const DataGridTable = ({ columns, data, onCheckboxChange, storageKey }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState(data);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    return JSON.parse(localStorage.getItem("columnVisibility")) || {};
  });

  // Persist pagination (page, pageSize) per storageKey or current path
  const defaultKey = storageKey || `datagrid_${window.location.pathname}`;
  const [paginationModel, handlePaginationModelChange] = usePaginationState(defaultKey, { pageSize: 50, page: 0 });
  const [pageSize, setPageSize] = useState(paginationModel.pageSize || 50);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    localStorage.setItem("columnVisibility", JSON.stringify(columnVisibilityModel));
  }, [columnVisibilityModel]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilteredData(
        data.filter((row) =>
          Object.values(row).some((value) =>
            value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
          )
        )
      );
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchTerm, data]);

  const handleColumnVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    handlePaginationModelChange({ ...paginationModel, pageSize: newPageSize });
  };

  const handleHeaderCheckboxClick = () => {
    setSelectAll(!selectAll);
    let updatedSelectedRows;
    if (!selectAll) {
      updatedSelectedRows = filteredData.map((row) => row.indexID);
    } else {
      updatedSelectedRows = [];
    }
    setSelectedRows(updatedSelectedRows);
    if (onCheckboxChange) {
      const rowData = updatedSelectedRows.map((rowID) =>
        data.find((row) => row.indexID === rowID)
      );
      onCheckboxChange(rowData);
    }
  };

  const handleChildCheckboxClick = (rowId) => {
    let newSelectedRows = [...selectedRows];
    if (newSelectedRows.includes(rowId)) {
      newSelectedRows = newSelectedRows.filter((id) => id !== rowId);
    } else {
      newSelectedRows.push(rowId);
    }
    setSelectedRows(newSelectedRows);
    setSelectAll(newSelectedRows.length === filteredData.length);
    if (onCheckboxChange) {
      const rowData = newSelectedRows.map((rowID) =>
        data.find((row) => row.indexID === rowID)
      );
      onCheckboxChange(rowData);
    }
  };

  const updatedColumns = [
    {
      field: "checkbox",
      headerName: (
        <Checkbox checked={selectAll} onClick={handleHeaderCheckboxClick} />
      ),
      minWidth: 90,
      renderCell: (params) => (
        <Checkbox
          checked={selectedRows.includes(params.row.indexID)}
          onClick={() => handleChildCheckboxClick(params.row.indexID)}
        />
      ),
    },
    ...columns,
  ];

  return (
    <div className="container-fluid bg-white rounded p-2 mb-3" style={{ position: "relative", width: "100%", maxWidth: "100vw" }}>
      <div style={{ position: "absolute", zIndex: 1, width: "400px" }}>
        <TextField
          type="text"
          placeholder="Search..."
          name="search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: "400px", "& .MuiOutlinedInput-root": { borderRadius: "4px", height: "30px", "& fieldset": { border: "1px solid #E0E0E0" } } }}
          variant="outlined"
          size="small"
        />
      </div>
      <div className="tableMenuHover" style={{ flexGrow: 1, width: "100%", height: "calc(100vh - 70px)" }}>
        <DataGrid
          rows={filteredData}
          columns={updatedColumns}
          getRowId={(row) => row.indexID}
          pagination
          paginationMode="client"
          paginationModel={paginationModel}
          onPaginationModelChange={(newModel) => {
            handlePaginationModelChange(newModel);
            setPageSize(newModel.pageSize || pageSize);
          }}
          pageSize={paginationModel.pageSize || pageSize}
          page={paginationModel.page || 0}
          pageSizeOptions={[10, 15, 25, 50]}
          rowsPerPageOptions={[10, 20, 25, 50, 100]}
          checkboxSelection={false}
          disableSelectionOnClick
          onPageSizeChange={handlePageSizeChange}
          components={{ Toolbar: GridToolbar, NoRowsOverlay: () => <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}><span style={{ fontFamily: "Poppins" }}>No Data Available</span></div> }}
          componentsProps={{ toolbar: { position: "right", style: { fontFamily: "Poppins", alignSelf: "end" } } }}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          sx={{ width: "100%" }}
        />
      </div>
    </div>
  );
};

export default DataGridTable;